import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import {
  getResourceType,
  JsonApiManyResourcesDocument,
  JsonApiOneResourceDocument,
  JsonApiService,
  resourceDifferences,
  ResourceType,
} from 'json-api';
import { Observable, Subject, map, of, pluck, tap } from 'rxjs';
import { AtomResource } from '../core/classes/atom-resource';
import { ExportParams, ExportService } from './export.service';

export interface AtomParams {
  include?: string[];
  filter?: Record<string, undefined | null | string | number | boolean>;
  fields?: Record<string, undefined | null | string | number>;
  sort?: string;
  direction?: SortDirection;
  pageIndex?: number;
  pageSize?: number;
}

export type UpdateData<T> = Partial<Omit<T, keyof AtomResource>>;
export type SaveData<T> = Partial<Omit<T, keyof AtomResource>> & {
  id?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AtomService {
  type<R extends AtomResource>(resourceType: ResourceType<R>): string {
    return resourceType.prototype.type;
  }

  get saved(): Observable<any> {
    return this.savedSubject.asObservable();
  }
  set saved(resource: any) {
    this.savedSubject.next(resource);
  }
  get updated(): Observable<any> {
    return this.updatedSubject.asObservable();
  }
  set updated(resource: any) {
    this.updatedSubject.next(resource);
  }
  get deleted(): Observable<any> {
    return this.deletedSubject.asObservable();
  }

  private savedSubject = new Subject<any>();
  private updatedSubject = new Subject<any>();
  private deletedSubject = new Subject<any>();

  constructor(
    protected jsonapi: JsonApiService,
    private exports: ExportService,
    @Inject(LOCALE_ID) protected locale: string
  ) {
  }

  all<R extends AtomResource>(
    type: ResourceType<R> | string,
    {
      filter,
      fields,
      include,
      sort,
      direction,
      pageIndex,
      pageSize,
    }: AtomParams = {}
  ): Observable<JsonApiManyResourcesDocument<R>> {
    return this.jsonapi.all(type, {
      filter,
      include: include && include.length > 0 ? include.join(',') : undefined,
      fields,
      sort: sort && {
        [this.locale]: direction === 'desc' ? `-${sort}` : sort,
      },
      page:
        pageIndex !== undefined && pageSize !== undefined
          ? {
              number: pageIndex + 1,
              size: pageSize,
            }
          : undefined,
    });
  }

  list<R extends AtomResource>(
    type: ResourceType<R> | string,
    params?: AtomParams
  ): Observable<R[]> {
    return this.all(type, params).pipe(map((res) => res['data']));
  }

  find<R extends AtomResource>(
    type: ResourceType<R>,
    id: string,
    { include }: Pick<AtomParams, 'include'> = {}
  ): Observable<R> {
    return this.jsonapi
      .find(type, id, {
        include: include && include.length > 0 ? include.join(',') : undefined,
      })
      .pipe(map((res) => res['data']));
  }

  delete<R extends AtomResource>(
    resource: R,
    included: boolean = false
  ): Observable<void> {
    return this.jsonapi.delete(resource).pipe(
      tap(() => {
        if (!included) {
          this.deletedSubject.next(resource);
        }
      })
    );
  }

  refresh<R extends AtomResource>(
    type: ResourceType<R>,
    id: string,
    { include }: Pick<AtomParams, 'include'> = {}
  ) {
    return this.find(type, id, { include }).pipe(
      tap((res) => this.updatedSubject.next(res))
    );
  }

  export(type: string, params: ExportParams): Observable<Blob> {
    return this.exports.excel(type, params);
  }

  save<R extends AtomResource>(
    resource: R,
    fields?: SaveData<R>
  ): Observable<JsonApiOneResourceDocument<R>> {
    if (fields) {
      Object.entries(fields).forEach(([property, value]) => {
        (resource as Record<string, any>)[property] = value;
      });
    }
    return this.jsonapi.save(resource).pipe(
      tap(() => {
        return this.savedSubject.next(resource);
      })
    );
  }

  update<R extends AtomResource>(
    resource: R,
    fields?: UpdateData<R>,
    propagate: boolean = true
  ): Observable<JsonApiOneResourceDocument<R>> {
    if (fields) {
      const { updatedValues, updatedFields } = resourceDifferences(
        resource,
        fields as Partial<R>
      );
      if (updatedFields.length === 0) {
        return of(new JsonApiOneResourceDocument<R>({ data: resource }));
      }

      const resourceType = getResourceType(resource.type);
      const clone = new resourceType({ id: resource.id }) as R;
      Object.entries(updatedValues).forEach(([property, value]) => {
        (clone as Record<string, any>)[property] = value;
      });
      return this.jsonapi.update(clone).pipe(
        tap(() => {
          Object.assign(resource, updatedValues);
          if (propagate) {
            this.savedSubject.next(resource);
          }
        })
      );
    }
    return this.jsonapi.update(resource);
  }

  batch(
    resource: { type: string; attributes?: any; relationships?: any },
    path: string,
    ids: string
  ): Observable<void> {
    return this.jsonapi.batch(resource, path, ids);
  }

  changeWorkflowState<R extends AtomResource>(
    resource: R,
    transition: string,
    fields?: UpdateData<R>,
  ): Observable<JsonApiOneResourceDocument<R>> {
        if (fields) {
      const { updatedValues, updatedFields } = resourceDifferences(
        resource,
        fields as Partial<R>
      );
      if (updatedFields.length === 0) {
        return of(new JsonApiOneResourceDocument<R>({ data: resource }));
      }

      const resourceType = getResourceType(resource.type);
      const clone = new resourceType({ id: resource.id }) as R;
      Object.entries(updatedValues).forEach(([property, value]) => {
        (clone as Record<string, any>)[property] = value;
      });
      return this.jsonapi.changeWorkflowState(clone, transition);
    }
    return this.jsonapi.changeWorkflowState(resource, transition)
  }

  getRessourcesOrAttributes(onlyEnum:boolean = true,attributes:boolean = false):Observable<string[] | string[][]>{
    return this.jsonapi.getResources().pipe(
      map(items =>{
        if(onlyEnum && attributes)
          return Object.entries(items.data).filter(([name,attributes]:[string,any])=> attributes.length).map(([name,attributes] :[string, any])=>attributes)
        if(onlyEnum)
          return Object.entries(items.data).filter(([name,attributes]:[string,any])=> attributes.length).map(([name])=>name)
        else
          return Object.entries(items.data).map(([name])=>name)
      }))}

  getRessources(onlyEnum:boolean = true): Observable<string[]> {
    return this.jsonapi.getResources().pipe(
      map(items =>{
        if(onlyEnum)
          return Object.entries(items.data).filter(([name,attributes]:[string,any])=> attributes.length).map(([name])=>(name))
        else
          return Object.entries(items.data).map(([name])=>(name))
      })
    )
  }

  getResourcesAttributes(resourceName:string):Observable<string[]>{
    return this.jsonapi.getResourcesEnumsAttributes(resourceName).pipe(map(resource =>resource.data.attributes))
  }
}
